/**
 * CloudExportIndicator — Fase 15.
 *
 * Indicador visual del export del SRT etiquetado al bucket. Acompaña al
 * mensaje de "✓ SRT descargado" en ExportScreen, mostrando si el archivo
 * también se guardó en la nube (POST /api/processings/:id/export).
 *
 * Por qué un componente separado de SaveIndicator:
 *   - SaveIndicator (Fase 14) está acoplado a atoms globales
 *     (saveStatusAtom, saveConflictAtom) del autoguardado de meta.json
 *     y maneja conflict resolution (409). Eso no aplica acá: el export
 *     siempre sobreescribe, no hay versionado, no hay conflict.
 *   - El estado del cloud export vive solo dentro de ExportScreen — no
 *     se comparte con otras pantallas. State local + props es más simple
 *     y no contamina el árbol de atoms.
 *
 * Estados:
 *   - "idle"   → no renderiza nada (sub-flujo manual o todavía no se intentó).
 *   - "saving" → "↑ Guardando en la nube…"
 *   - "saved"  → "☁ Guardado en la nube"
 *   - "error"  → mensaje + botón "Reintentar" si onRetry está provisto.
 *
 * `updatedAt` del estado "saved" no se muestra todavía. Está disponible
 * por si en el futuro queremos mostrar "Guardado a las HH:MM".
 */

import styled from "styled-components";
import { Button } from "./Button.tsx";

export type CloudExportState =
  | { kind: "idle" }
  | { kind: "saving" }
  | { kind: "saved"; updatedAt: string }
  | { kind: "error"; message: string };

type Props = {
  state: CloudExportState;
  /**
   * Callback opcional para reintentar el export tras un error. Si no se
   * provee, el indicador en estado "error" muestra solo el mensaje sin
   * botón. Llamado típicamente desde ExportScreen con el SRT actual.
   */
  onRetry?: () => void;
};

export function CloudExportIndicator({ state, onRetry }: Props) {
  if (state.kind === "idle") return null;

  if (state.kind === "saving") {
    return (
      <Row $tone="muted">
        <Icon>↑</Icon>
        <Message>Guardando en la nube…</Message>
      </Row>
    );
  }

  if (state.kind === "saved") {
    return (
      <Row $tone="success">
        <Icon>☁</Icon>
        <Message>Guardado en la nube</Message>
      </Row>
    );
  }

  // error
  return (
    <Row $tone="error">
      <Icon>⚠</Icon>
      <Message>No se pudo guardar en la nube: {state.message}</Message>
      {onRetry && (
        <Button $variant="ghost" onClick={onRetry}>
          Reintentar
        </Button>
      )}
    </Row>
  );
}

// ---------------------------------------------------------------------------
// Styled
// ---------------------------------------------------------------------------

const Row = styled.div<{ $tone: "muted" | "success" | "error" }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.sm};
  padding: ${({ theme }) => theme.space.sm} ${({ theme }) => theme.space.md};
  background: ${({ theme, $tone }) =>
    $tone === "success"
      ? theme.color.successMuted
      : $tone === "error"
        ? theme.color.errorMuted
        : "transparent"};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme, $tone }) =>
    $tone === "success"
      ? theme.color.success ?? theme.color.accent
      : $tone === "error"
        ? theme.color.error
        : theme.color.textMuted};
`;

const Icon = styled.span`
  flex-shrink: 0;
  font-size: ${({ theme }) => theme.font.size.md};
`;

const Message = styled.span`
  flex: 1;
  font-family: ${({ theme }) => theme.font.mono};
`;
